import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { useTutorialsContext } from "../../TutorialsContext";
import { getParticipantIdFromSearchParams } from "../../../../utils/urlParams";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

/** Minimum words for the open-ended pre-question (aligned with backend attention-check). */
const MIN_OPEN_ANSWER_WORDS = 10;

const proficiencyWordNoticeStyle: React.CSSProperties = {
    color: '#dc2626',
    fontWeight: 600,
    fontSize: 14,
    margin: 0,
    marginTop: 4,
    marginBottom: 8,
    lineHeight: 1.45,
};

const ProficiencyWordNotice = () => (
    <p style={proficiencyWordNoticeStyle}>
        (Please write at least {MIN_OPEN_ANSWER_WORDS} words in English in your own words. Do not use AI or other tools.)
    </p>
);

export const HumanCheckMC = forwardRef((_props, ref) => {
    const [mcAnswer, setMcAnswer] = useState("");
    const [error, setError] = useState("");
    const { setDisabled } = useTutorialsContext();
    const [touched, setTouched] = useState(false);

    const mcOptions = [
        "Strongly agree",
        "Agree",
        "Neutral",
        "Disagree",
        "Strongly disagree"
    ];

    useEffect(() => {
        setDisabled(!mcAnswer);
    }, [mcAnswer, setDisabled]);

    const validateAndCheck = async () => {
        setTouched(true);
        if (!mcAnswer) {
            setError("Please answer the multiple choice question to proceed.");
            return false;
        } else if (mcAnswer !== "Strongly disagree") {
            setError("You failed the attention check. Please select the correct answer to proceed.");
            return "fail-attention-check";
        } else {
            setError("");
            return true;
        }
    };

    useImperativeHandle(ref, () => ({ validateAndCheck, getMCAnswer: () => mcAnswer }), [mcAnswer, error]);

    return (
        <div style={{ padding: "24px 100px", width: "90vw", boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <span
                className="text"
                style={{
                    display: "inline-block",
                    textAlign: "justify",
                    textJustify: "inter-word",
                    marginBottom: 32,
                }}
            >
                <div style={{ marginBottom: 24 }}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 22, fontWeight: 600 }}>
                            Please answer the following questions:
                        </span>
                        <br /><br />
                        Please indicate your level of agreement with the following statement:<br />
                        <em>I live on Saturn for two years since my born.</em>
                    </div>
                    {mcOptions.map(option => (
                        <label key={option} style={{ display: 'block', marginBottom: 4 }}>
                            <input
                                type="radio"
                                name="mcSaturn"
                                value={option}
                                checked={mcAnswer === option}
                                onChange={e => setMcAnswer(e.target.value)}
                                style={{ marginRight: 8 }}
                            />
                            {option}
                        </label>
                    ))}
                </div>
            </span>
            <input type="hidden" value={mcAnswer === "Strongly disagree" ? "passed" : "failed"} name="attentionCheckFlag" />
            {touched && error && (
                <div style={{ color: error.startsWith("You failed") ? 'orange' : 'red', marginTop: 8, fontWeight: 500, fontSize: 16 }}>
                    {error}
                </div>
            )}
        </div>
    );
});

export const HumanCheckOpen = forwardRef(({ mcAnswer }: { mcAnswer: string }, ref) => {
    const [answer, setAnswer] = useState("");
    const [error, setError] = useState("");
    const [checking, setChecking] = useState(false);
    const { setDisabled } = useTutorialsContext();
    const [touched, setTouched] = useState(false);
    const [lastChecked, setLastChecked] = useState<string | null>(null);
    const lastPassedRef = useRef<boolean | null>(null);

    const prolificId = getParticipantIdFromSearchParams(window.location.search) || '';

    useEffect(() => {
        const wordCount = answer.trim().split(/\s+/).filter(Boolean).length;
        setDisabled(!(answer.trim().length > 0 && wordCount >= MIN_OPEN_ANSWER_WORDS));
    }, [answer, setDisabled]);

    useEffect(() => {
        if (lastChecked !== null && answer !== lastChecked) {
            lastPassedRef.current = null;
            setLastChecked(null);
        }
    }, [answer, lastChecked]);

    const checkProficiency = async (freeTextAnswer: string) => {
        console.log('HumanCheckOpen: checkProficiency', API_BASE_URL);
        console.log('HumanCheckOpen: checkProficiency', `${API_BASE_URL}/attention-check`);
        setChecking(true);
        try {
            const res = await fetch(`${API_BASE_URL}/attention-check`, {
                method: "POST",
                headers: { 
                    "Content-Type": "application/json",
                    "prolific-id": prolificId
                },
                body: JSON.stringify({ freeTextAnswer })
            });
            const data = await res.json();
            setChecking(false);
            return data;
        } catch (e) {
            setChecking(false);
            // Fail open: do not block participation when the check cannot run
            return { passed: true };
        }
    };

    const validateAndCheck = async () => {
        setTouched(true);
        const wordCount = answer.trim().split(/\s+/).filter(Boolean).length;
        if (wordCount < MIN_OPEN_ANSWER_WORDS) {
            setError(`Please write at least ${MIN_OPEN_ANSWER_WORDS} words in English to proceed.`);
            return false;
        }
        if (lastChecked === answer && lastPassedRef.current === true) {
            return true;
        }
        setError("");
        setChecking(true);
        const result = await checkProficiency(answer);
        setChecking(false);
        setLastChecked(answer);
        lastPassedRef.current = !!result.passed;
        if (result.passed) {
            setError("");
            setDisabled(false);
            return true;
        }
        setError(result.reason || "Please expand your answer slightly and try again.");
        const wordCountAfter = answer.trim().split(/\s+/).filter(Boolean).length;
        setDisabled(!(answer.trim().length > 0 && wordCountAfter >= MIN_OPEN_ANSWER_WORDS));
        return false;
    };

    useImperativeHandle(ref, () => ({ validateAndCheck, getFreeTextAnswer: () => answer }), [answer, error, lastChecked]);

    return (
        <div style={{ padding: "24px 100px", width: "90vw", boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <span
                className="text"
                style={{
                    display: "inline-block",
                    textAlign: "justify",
                    textJustify: "inter-word",
                    marginBottom: 8,
                }}
            >
                <div style={{ marginBottom: 0 }}>
                    Why are you interested in this study?
                </div>
                <ProficiencyWordNotice />
            </span>
            <textarea
                value={answer}
                onChange={e => setAnswer(e.target.value)}
                placeholder="Type your answer here..."
                style={{ width: "100%", minHeight: 100, fontSize: 16, borderRadius: 6, border: '1px solid #ccc', padding: 12, marginBottom: 16 }}
                disabled={!!error && error.startsWith("You failed") || checking}
            />
            <input type="hidden" value={mcAnswer === "Strongly disagree" ? "passed" : "failed"} name="attentionCheckFlag" />
            <input type="hidden" value={answer} name="freeTextAnswer" />
            {checking && (
                <div style={{ color: '#888', marginTop: 8, fontWeight: 500, fontSize: 16 }}>
                    Checking English proficiency...
                </div>
            )}
            {touched && error && (
                <div style={{ color: '#dc2626', marginTop: 8, fontWeight: 700, fontSize: 16 }}>
                    {error}
                </div>
            )}
        </div>
    );
});

export const CombinedPreQuestions = forwardRef((_props, ref) => {
    const [mcAnswer, setMcAnswer] = useState("");
    const [mcError, setMcError] = useState("");
    const [openAnswer, setOpenAnswer] = useState("");
    const [openError, setOpenError] = useState("");
    const [checking, setChecking] = useState(false);
    const { setDisabled } = useTutorialsContext();
    const [touched, setTouched] = useState(false);
    const [lastChecked, setLastChecked] = useState<string | null>(null);
    const lastPassedRef = useRef<boolean | null>(null);

    const prolificId = getParticipantIdFromSearchParams(window.location.search) || '';

    const mcOptions = [
        "Strongly agree",
        "Agree",
        "Neutral",
        "Disagree",
        "Strongly disagree"
    ];

    useEffect(() => {
        const wordCount = openAnswer.trim().split(/\s+/).filter(Boolean).length;
        setDisabled(!(mcAnswer && openAnswer.trim().length > 0 && wordCount >= MIN_OPEN_ANSWER_WORDS));
    }, [mcAnswer, openAnswer, setDisabled]);

    useEffect(() => {
        if (lastChecked !== null && openAnswer !== lastChecked) {
            lastPassedRef.current = null;
            setLastChecked(null);
        }
    }, [openAnswer, lastChecked]);

    const checkProficiency = async (freeTextAnswer: string) => {
        setChecking(true);
        try {
            const res = await fetch(`${API_BASE_URL}/attention-check`, {
                method: "POST",
                headers: { 
                    "Content-Type": "application/json",
                    "prolific-id": prolificId
                },
                body: JSON.stringify({ freeTextAnswer })
            });
            const data = await res.json();
            setChecking(false);
            return data;
        } catch (e) {
            setChecking(false);
            return { passed: true };
        }
    };

    const validateAndCheck = async () => {
        setTouched(true);
        // MC validation
        if (!mcAnswer) {
            setMcError("Please answer Pre-question 1 to proceed.");
            return false;
        }
        if (mcAnswer !== "Strongly disagree") {
            setMcError("For Pre-question 1, please select “Strongly disagree” (the statement is not true).");
            return "ineligible";
        }
        setMcError("");

        const wordCount = openAnswer.trim().split(/\s+/).filter(Boolean).length;
        if (wordCount < MIN_OPEN_ANSWER_WORDS) {
            setOpenError(`Please write at least ${MIN_OPEN_ANSWER_WORDS} words in English to proceed.`);
            return false;
        }
        setOpenError("");

        if (lastChecked === openAnswer && lastPassedRef.current === true) {
            return true;
        }
        setChecking(true);
        const result = await checkProficiency(openAnswer);
        setChecking(false);
        setLastChecked(openAnswer);
        lastPassedRef.current = !!result.passed;
        if (result.passed) {
            setOpenError("");
            setDisabled(false);
            return true;
        }
        setOpenError(result.reason || "Please add a bit more detail about why you are interested and try again.");
        const wordCountAfter = openAnswer.trim().split(/\s+/).filter(Boolean).length;
        setDisabled(!(mcAnswer && openAnswer.trim().length > 0 && wordCountAfter >= MIN_OPEN_ANSWER_WORDS));
        return false;
    };

    useImperativeHandle(ref, () => ({ validateAndCheck, getMCAnswer: () => mcAnswer, getFreeTextAnswer: () => openAnswer }), [mcAnswer, openAnswer, mcError, openError, lastChecked]);

    return (
        <div style={{ padding: "24px 100px", width: "90vw", boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            {/* Prompt for both questions */}
            <span className="text" style={{ display: "inline-block", textAlign: "justify", textJustify: "inter-word", marginBottom: 32 }}>
                <div style={{ marginBottom: 24 }}>
                    <span style={{ fontSize: 18, fontWeight: 600 }}>
                        Please answer the following questions:
                    </span>
                    <br /><br />
                    <div style={{ marginBottom: 8 }}>
                        Please indicate your level of agreement with the following statement:<br />
                        <em>I lived on Saturn for 2 years after I was born</em>
                    </div>
                    {mcOptions.map(option => (
                        <label key={option} style={{ display: 'block', marginBottom: 4 }}>
                            <input
                                type="radio"
                                name="mcSaturn"
                                value={option}
                                checked={mcAnswer === option}
                                onChange={e => setMcAnswer(e.target.value)}
                                style={{ marginRight: 8 }}
                            />
                            {option}
                        </label>
                    ))}
                </div>
            </span>
            {/* Open-ended question, no title */}
            <span className="text" style={{ display: "inline-block", textAlign: "justify", textJustify: "inter-word", marginBottom: 8 }}>
                <div style={{ marginBottom: 0 }}>
                    Why are you interested in this study?
                </div>
                <ProficiencyWordNotice />
            </span>
            <textarea
                value={openAnswer}
                onChange={e => setOpenAnswer(e.target.value)}
                placeholder="Type your answer here..."
                style={{ width: "100%", minHeight: 100, fontSize: 16, borderRadius: 6, border: '1px solid #ccc', padding: 12, marginBottom: 16 }}
                disabled={!!openError && openError.startsWith("You failed") || checking}
            />
            <input type="hidden" value={mcAnswer === "Strongly disagree" ? "passed" : "failed"} name="attentionCheckFlag" />
            <input type="hidden" value={openAnswer} name="freeTextAnswer" />
            {/* Show all errors together */}
            {touched && (mcError || openError) && (
                <div style={{ color: '#dc2626', marginTop: 8, fontWeight: 700, fontSize: 16 }}>
                    {mcError && <div>{mcError}</div>}
                    {openError && <div>{openError}</div>}
                </div>
            )}
            {checking && (
                <div style={{ color: '#888', marginTop: 8, fontWeight: 500, fontSize: 16 }}>
                    Checking English proficiency...
                </div>
            )}
        </div>
    );
});